import React, { useCallback, useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import TableLayout from "../../layouts/TableLayout/NewTableLayout";
import getTableConfig from "./table.configs";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import CreateModal from "./CreateModal";
import {
  useGetJobsQuery,
  useUpdateJobMutation,
} from "../../redux/services/jobs/jobsApi";
import { usePrefetch } from "../../redux/api";
import { DeleteJobModal } from "../../modals";
import {
  ClientsFilterList,
  StatusFilterList,
  TagsFilterList,
} from "../../components";
import { removeEmptyValues, capitalizeFirstLetter } from "../../utils";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/access-control";
import { toast } from "react-toastify";
import { useConfig } from "../../hooks/";
import { getProductTerminology } from "../../constants/product-terminology";
import { renderCustomColumnDefs } from "../../shared/Table/table.utils";
import { useFetchAll } from "../../hooks/useFetchAll";
import { useGetClientQuery } from "../../redux/services/clients/clientsApi";
import { getStage, normalizeJobStatusForStages } from "./job-stages";
import { jobListSearch } from "./jobListQuery";

const Jobs = ({ clientId, employeeId }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { appType } = useConfig();
  const terms = getProductTerminology(appType);
  const { hasPermission, hasAnyPermission } = usePermissions();
  const canViewClientFilter = hasAnyPermission([
    PERMISSIONS.JOBS_EDIT,
    PERMISSIONS.JOBS_READ,
  ]);
  const canCreateOrAssignJobs = hasAnyPermission([
    PERMISSIONS.JOBS_CREATE,
    PERMISSIONS.JOBS_ASSIGN,
  ]);
  const [deselectItems, setDeselectItems] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Fetch client data if clientId is provided
  const { data: clientData } = useGetClientQuery(
    { id: clientId },
    { skip: !clientId }
  );

  // Get the client object from the response
  const client = clientData;

  const tableConfig = useMemo(() => getTableConfig(appType), [appType]);
  const [updateJob] = useUpdateJobMutation();

  const filterTypes = useMemo(
    () =>
      [
        {
          id: appType === "trucking" ? "transactionDate" : "appointmentDate",
          type: "date",
          label:
            appType === "trucking" ? "Transaction Date" : "Appointment Date",
          defaultText: "All Time",
          defaultValue: {
            id: "allTime",
            value: null,
          },
        },
        {
          id: "status",
          type: "multiple",
          label: "Status",
          defaultText: "All",
          content: StatusFilterList,
        },
        {
          id: "client",
          remove: !canViewClientFilter || !!clientId,
          type: "multiple",
          label: "Client",
          defaultText: "All Clients",
          content: ClientsFilterList,
        },
        {
          id: "tagIds",
          type: "multiple",
          label: "Tags",
          defaultText: "All Tags",
          content: TagsFilterList,
        },
      ].filter((i) => !i.remove),
    [appType, clientId]
  );

  const initialFilters = useMemo(() => {
    const initial = [];
    const startDateParam = searchParams.get("startDate");
    const endDateParam = searchParams.get("endDate");
    if (startDateParam && endDateParam) {
      const start = new Date(startDateParam);
      const end = new Date(endDateParam);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
        const dateFilterDef = filterTypes.find((f) => f.type === "date");
        if (dateFilterDef) {
          const isSingle =
            start.toDateString && end.toDateString
              ? start.toDateString() === end.toDateString()
              : false;
          initial.push({
            ...dateFilterDef,
            type: isSingle ? "singleDate" : "customRange",
            value: { start, end },
            displayText: isSingle
              ? start.toLocaleDateString()
              : `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`,
          });
        }
      }
    }

    const statusParam = searchParams.get("status");
    if (statusParam) {
      const statusFilterDef = filterTypes.find((f) => f.id === "status");
      if (statusFilterDef) {
        const normalizedStatusIds = statusParam
          .split(",")
          .map((statusToken) => normalizeJobStatusForStages(statusToken, appType))
          .filter(Boolean);

        const statusTitles = normalizedStatusIds
          .map((statusId) => getStage(statusId, appType)?.title || statusId)
          .join(",");

        initial.push({
          ...statusFilterDef,
          type: "multiple",
          value: normalizedStatusIds.join(","),
          displayText: statusTitles,
        });
      }
    }

    const tagParam = searchParams.get("tagIds");
    if (tagParam) {
      const tagFilterDef = filterTypes.find((f) => f.id === "tagIds");
      if (tagFilterDef) {
        initial.push({
          ...tagFilterDef,
          type: "multiple",
          value: tagParam,
          displayText: "Selected Tags",
        });
      }
    }

    return initial;
  }, [searchParams, filterTypes, appType]);

  const initialQueryFilters = useMemo(() => {
    if (!Array.isArray(initialFilters) || initialFilters.length === 0) return [];
    // Only pass the minimum shape the query layer needs.
    return initialFilters
      .filter((filter) => filter?.id && filter?.value)
      .map((filter) => ({ id: filter.id, value: filter.value }));
  }, [initialFilters]);

  const {
    pagination: { pageIndex, pageSize, setPagination },
    search: { handleGlobalSearch },
    filters: { filters, setFilters },
    queryResult: { data, isLoading, isError },
    queryParams,
  } = useFetchAll({
    queryHook: useGetJobsQuery,
    defaultPageSize: 20,
    defaultFilters: [
      { id: "client", value: clientId },
      { id: "employeeId", value: employeeId },
    ],
    initialFilters: initialQueryFilters,
  });

  useEffect(() => {
    if (clientId) {
      setFilters((prev) => [...prev, { id: "client", value: clientId }]);
    }
    if (employeeId) {
      setFilters((prev) => [...prev, { id: "employeeId", value: employeeId }]);
    }
  }, [clientId, employeeId]);

  const listSearch = jobListSearch(queryParams);

  const handleEdit = useCallback(
    (itemId) => {
      navigate(`/jobs/${itemId}${listSearch}`);
    },
    [navigate, listSearch]
  );

  const handleUpdate = useCallback(
    async (e, setLoading) => {
      try {
        const res = await updateJob({
          id: e.row.original._id,
          data: e.data,
        }).unwrap();

        toast(res.message, {
          type: res.status,
          toastId: `update-job-${res.status}`,
        });
      } finally {
        setTimeout(() => setLoading(false), 500);
      }
    },
    [updateJob]
  );

  const handleDeleteClick = useCallback(() => {
    setDeleteVisible(true);
  }, []);

  const cannotCreateOrEdit = !canCreateOrAssignJobs;

  const handleDeselect = useCallback(() => {
    setDeselectItems(true);
    setTimeout(() => setDeselectItems(false), 500);
  }, []);

  return (
    <div className="w-full h-full min-h-0">
      <TableLayout
        id="jobs"
        title={terms.jobPlural}
        buttonSize="xs"
        loading={isLoading}
        columns={tableConfig.COLUMN_DEFINITIONS}
        data={
          Array.isArray(data?.jobs)
            ? data.jobs
            : Array.isArray(data?.docs)
              ? data.docs
              : []
        }
        emptyState={
          isError
            ? {
                title: `Couldn't load ${terms.jobPlural.toLowerCase()}`,
                description:
                  "The list request timed out or failed. Refresh the page or try again.",
              }
            : {
                title: `No ${terms.jobPlural}`,
                description: `Get started by adding a ${terms.jobLower}.`,
              }
        }
        pageSizeOptions={tableConfig.PAGE_SIZE_OPTIONS}
        pagination={data}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPaginationChange={setPagination}
        handleEdit={handleEdit}
        onUpdate={handleUpdate}
        mainAction={{ onClick: () => setCreateModalOpen(true) }}
        hideActions={cannotCreateOrEdit}
        hideMainAction={cannotCreateOrEdit}
        globalSearchFilters={["transactionDate", "loadNumber"]}
        fullTextSearch={handleGlobalSearch}
        sendSelectedItems={setSelectedItems}
        handleDeleteClick={handleDeleteClick}
        clearSelectedItems={deselectItems}
        filters={filterTypes}
        onFiltersChange={setFilters}
        initialFilters={initialFilters}
        hideCheckbox={!canCreateOrAssignJobs}
        meta={{
          updateData: handleUpdate,
          jobListSearch: listSearch,
        }}
      />
      <CreateModal
        clientId={clientId}
        client={client}
        modalOpen={createModalOpen}
        setModalOpen={setCreateModalOpen}
        showFullForm={false}
      />
      <DeleteJobModal
        deselectItems={handleDeselect}
        visible={deleteVisible}
        setVisible={setDeleteVisible}
        jobs={selectedItems}
      />
    </div>
  );
};

export default Jobs;
